<template>
  <div id="interview-duration" class="widget widget-height">
    <div class="title row-block">
      <h1>{{ $tr('analytics.widgets.totalVideoTime.title') }}</h1>
      <img
        v-tooltip="{
          content: $tr('analytics.widgets.totalVideoTime.helper'),
          placement: 'top-center',
          classes: 'tight'
        }"
        class="icon-first canvas-filter"
        src="/static/images/elements/icon_info.svg"
        alt="Info"
      />

      <img class="canvas-filter" src="/static/images/elements/icon_download.svg" alt="Download" @click="saveAsPng" />
    </div>

    <div v-if="loading" class="spinner-wraper">
      <div class="spinner"></div>
    </div>

    <div v-else class="main-content">
      <div class="row-block time">
        <div class="title-thin days">{{ totalDays }}</div>
        <div class="title-thin">{{ hoursWithMinutes }}</div>
      </div>
      <div class="row-block labels">
        <span>{{ $tr('analytics.widgets.totalVideoTime.days') }}</span>
      </div>

      <div class="row-block compare">
        <img
          v-if="pastToCurrentComparePercent > 0"
          :src="`/static/images/elements/analytics/stat_up.${imgExtension}`"
          alt="Up"
        />

        <img
          v-else-if="pastToCurrentComparePercent < 0"
          :src="`/static/images/elements/analytics/stat_down.${imgExtension}`"
          alt="Down"
        />

        <span v-if="pastToCurrentComparePercent" class="percentage">
          <i v-show="pastToCurrentComparePercent > 0">+ </i>{{ pastToCurrentComparePercent }} %
        </span>

        <span class="helper">
          {{ hint }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import Pluralize from '@/mixins/pluralize';
import hintCompareMixin from './hintCompareMixin.js';
import mixinIE11Detector from './mixinIE11Detector.js';
import mixinSaveToPng from './mixinSaveToPng.js';

const isNumeric = n => !isNaN(parseFloat(n)) && isFinite(n);

export default {
  name: 'WidgetTotalInterviewDuration',

  mixins: [hintCompareMixin, Pluralize, mixinSaveToPng, mixinIE11Detector],

  props: {
    filtersParams: Object,
    filtersPastParams: Object,
    pastDurationMoment: Object
  },

  computed: {
    imgExtension() {
      return this.isIE11 ? 'png' : 'svg';
    },

    momentDuration() {
      const f = this.duration ? this.$moment.duration(this.duration, 'seconds') : null;

      return f && f.isValid() ? f : null;
    },

    totalDays() {
      return this.momentDuration ? this.momentDuration.days() : 0;
    },

    hoursWithMinutes() {
      const balanceTimeWithoutDays = this.momentDuration
        ? this.momentDuration.clone().subtract(this.totalDays, 'days')
        : null;

      // Extract minutes this way because on .format('HH:mm')
      // if we have 37 minutes at total (with subtracted days)
      // it display only '37', but we need '00:37'
      const hours = balanceTimeWithoutDays ? balanceTimeWithoutDays.format('HH') : '00';
      const minutes = balanceTimeWithoutDays ? balanceTimeWithoutDays.minutes() : '00';

      return `${hours}:${minutes < 10 && minutes !== '00' ? '0' : ''}${minutes}`;
    },

    pastToCurrentComparePercent() {
      const a = this.duration;
      const b = this.pastDuration;

      const res = a > b ? ((a - b) / a) * 100 : ((a - b) / a) * 100;

      return res && isNumeric(res) ? res.toFixed(1) : null;
    },

    hint() {
      return this.getComparePeriodHint(this.pastDurationMoment, this.pastToCurrentComparePercent);
    },

    ...mapGetters({
      duration: 'analytics/totalInterviewDuration/duration',
      pastDuration: 'analytics/totalInterviewDuration/pastDuration',
      loading: 'analytics/totalInterviewDuration/loading'
    })
  },

  watch: {
    filtersParams() {
      this.fetch();
    },

    filtersPastParams() {
      this.fetchPast();
    }
  },

  beforeMount() {
    this.fetch();
    this.fetchPast();
  },

  methods: {
    fetch() {
      this.$store.dispatch('analytics/totalInterviewDuration/fetch', this.filtersParams);
    },

    fetchPast() {
      this.$store.dispatch('analytics/totalInterviewDuration/fetchCompare', this.filtersPastParams);
    },

    saveAsPng() {
      this.mixinSaveAsPng({
        filename: `interview-duration_${this.filtersParams.created_after}_${this.filtersParams.created_before}`,
        saveId: 'interview-duration',
        filterClass: 'canvas-filter'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.widget {
  .spinner-wraper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
  }

  .spinner {
    margin: auto;
    height: 50px;
    width: 50px;
    margin-top: 0;
  }

  .main-content {
    h2 {
      margin-top: 4px;
    }

    .title-thin {
      font-weight: 300;
      line-height: 68px;
      font-size: 48px;
      color: #233346;

      span {
        font-weight: 300;
      }
    }

    .days {
      margin-right: 30px;
    }

    .description + .labels {
      position: relative;
      top: 2px;

      span {
        color: #bdbdbd;
        line-height: 17px;
        font-size: 12px;

        &.right-align {
          margin-left: auto;
        }

        &.like-minutes-width {
          // Width like minutes in line below
          width: 67px;
          text-align: right;
        }
      }
    }

    .row-block.time {
      justify-content: flex-start;
      margin-bottom: -10px;
    }

    .row-block.compare {
      margin-top: 16px;

      span.percentage {
        line-height: 25px;
        font-size: 18px;
        white-space: nowrap;
        margin-left: 10px;

        i {
          font-style: normal;
        }

        + .helper {
          margin-left: 14px;
        }
      }

      span.helper {
        line-height: 12px;
        font-size: 12px;
        color: #607183;
      }

      img {
        width: 16px;
      }
    }
  }
}
</style>
